if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.bindMainGalleryNavigation();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      bindMainGalleryNavigation() {
        this.elements.viewer.querySelectorAll(':scope > .slider-buttons .slider-button').forEach((button) => {
          button.removeAttribute('disabled');
          button.addEventListener('click', this.onMainGalleryNavigationClick.bind(this), { capture: true });
        });
        this.mainGalleryNavigationObserver = new MutationObserver(() => this.enableMainGalleryNavigation());
        this.elements.viewer.querySelectorAll(':scope > .slider-buttons .slider-button').forEach((button) => {
          this.mainGalleryNavigationObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] });
        });
      }

      onMainGalleryNavigationClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const mediaTargets = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).map(
          (thumbnail) => thumbnail.dataset.target
        );
        if (mediaTargets.length < 2) return;

        const activeMediaId = this.elements.viewer.querySelector('.product__media-item[data-media-id].is-active')?.dataset.mediaId;
        const currentIndex = Math.max(
          mediaTargets.findIndex((mediaId) => mediaId === activeMediaId),
          0
        );
        const direction = event.currentTarget.name === 'previous' ? -1 : 1;
        const nextIndex = (currentIndex + direction + mediaTargets.length) % mediaTargets.length;

        this.setActiveMedia(mediaTargets[nextIndex], false);
        this.enableMainGalleryNavigation();
      }

      enableMainGalleryNavigation() {
        this.elements.viewer.querySelectorAll(':scope > .slider-buttons .slider-button').forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.enableMainGalleryNavigation();
        window.setTimeout(() => this.enableMainGalleryNavigation());

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const thumbnailList = this.elements.thumbnails.slider;
        const isVertical = window.matchMedia('(min-width: 990px)').matches &&
          window.getComputedStyle(thumbnailList).flexDirection === 'column';

        if (isVertical) {
          const listRect = thumbnailList.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          const isVisible = thumbnailRect.top >= listRect.top + 10 && thumbnailRect.bottom <= listRect.bottom - 10;

          if (!isVisible) {
            thumbnailList.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        thumbnailList.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
